import React, { useState } from "react";
import { motion } from "framer-motion";
import { Play, Youtube, ArrowUpRight } from "lucide-react";
import { SHOWCASE } from "@/lib/brand";
import { Reveal, SectionLabel } from "@/components/Motion";

export const Showcase = () => {
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const video = SHOWCASE.videos[active];

  return (
    <section id="showcase" className="relative border-b border-white/8 py-24 lg:py-36">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-10 pb-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <SectionLabel index="§ 05">Showcase</SectionLabel>
            <Reveal delay={0.05}>
              <h2 className="mt-6 font-display text-[clamp(2rem,4.6vw,3.6rem)] font-extrabold uppercase leading-[0.96] tracking-[-0.015em] text-white">
                Don't read it.
                <br />
                <span className="text-white/35">Watch it land.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <Reveal delay={0.12}>
              <p className="text-sm leading-relaxed text-white/45">
                Raw in-game footage — scenes staged and played entirely inside Fight Studio, captured
                with ordinary recording software. No edits to the choreography, no cheats. That's
                exactly what your server gets.
              </p>
              <a
                href={SHOWCASE.channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="youtube-channel-link"
                className="group mt-6 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:text-white"
              >
                <Youtube className="h-4 w-4" />
                Full channel
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </Reveal>
          </div>
        </div>

        <Reveal y={40}>
          <div className="relative border border-white/12 bg-[#0b0b0e] p-2 sm:p-3">
            <div className="relative aspect-video w-full overflow-hidden bg-black">
              {playing ? (
                <iframe
                  key={video.id + active}
                  data-testid="youtube-embed"
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <button
                  data-testid="showcase-play-overlay"
                  onClick={() => setPlaying(true)}
                  className="group absolute inset-0 h-full w-full"
                >
                  <img
                    src={`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`}
                    alt={video.title}
                    className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:scale-[1.03] group-hover:opacity-80"
                    onError={(e) => {
                      e.currentTarget.src = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
                    }}
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/40" />
                  <span className="absolute left-1/2 top-1/2 grid h-20 w-20 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-white/25 backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:border-white/70">
                    <Play className="h-6 w-6 fill-white text-white" />
                  </span>
                  <span className="absolute bottom-6 left-6 text-left">
                    <span className="label block">{video.meta}</span>
                    <span className="mt-2 block font-display text-lg font-semibold uppercase tracking-tight text-white sm:text-2xl">
                      {video.title}
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        </Reveal>

        <div className="mt-3 flex items-center justify-between gap-4 border border-white/10 px-5 py-3.5">
          <span className="truncate text-[13px] text-white/45">{video.title}</span>
          <a
            href={`https://www.youtube.com/watch?v=${video.id}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="watch-on-youtube-link"
            className="group inline-flex shrink-0 items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50 transition-colors duration-300 hover:text-white"
          >
            <Youtube className="h-3.5 w-3.5" />
            Watch on YouTube
            <ArrowUpRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        {SHOWCASE.videos.length > 1 && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {SHOWCASE.videos.map((v, i) => (
              <motion.button
                key={i}
                data-testid={`showcase-thumb-${i}`}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => {
                  setActive(i);
                  setPlaying(false);
                }}
                className={`group relative overflow-hidden border p-3 text-left transition-colors duration-300 ${
                  active === i
                    ? "border-[hsl(var(--orv-blue))]/60 bg-white/5"
                    : "border-white/10 hover:border-white/30"
                }`}
              >
                <span className="label block">{v.meta}</span>
                <span className="mt-2 block text-[13px] leading-snug text-white/70 transition-colors duration-300 group-hover:text-white">
                  {v.title}
                </span>
              </motion.button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
